import { resolveImageUrl } from "../../utils/ImageUrl";
import { exportRows } from "../../utils/ExportRows";
import { useAuth } from "../../auth/useAuth";
import { useLocation } from "react-router-dom";
import { getRoleUiForPath } from "../../access/RoleUiCatalogue";
import { Section, Button, Dropdown, Input, MultiSelect, Switch, TextArea, Notification, HStack, VStack, FileUpload, } from "@akin2unde/flowui-react";
import type { DropdownModel } from "@akin2unde/flowui-react";
import { useManagementPage } from "./ManagementPage";
import type { ManagementPageProps } from "./ManagementPageTypes";
import RightSideDialogHTML from "../RightSideDialog/RightSideDialogHTML";
import type { DBModel } from "../../models/DBModel";
import "./ManagementPageCss.css";
import { Status } from "../../models/Status";
import { DataState } from "../../models/DataState";
import { PrepareImageFile } from "../../utils/ImageFile";
export default function ManagementPageHTML<T extends DBModel>(props: ManagementPageProps<T>) {
    const auth = useAuth();
    const location = useLocation();
    const ui = getRoleUiForPath(location.pathname);
    const allowed = (action: string) => !ui ||
        auth.hasPermission(`${ui.Code}.${action}`) ||
        (auth.state.RoleUis === undefined &&
            auth.hasPermission(ui.Legacy.endsWith(".View")
                ? ui.Legacy.replace(/\.View$/, ".Manage")
                : ui.Legacy));
    props = {
        ...props,
        CanCreate: (props.CanCreate ?? true) && allowed("Create"),
        CanEdit: (props.CanEdit ?? true) && allowed("Edit"),
        CanDelete: (props.CanDelete ?? true) && allowed("Delete"),
    };
    const { state, filteredRows, setStateProperty, openCreateDialog, openEditDialog, closeDialog, setFormValue, save, remove, dialogFields, setFilter, search, previousPage, nextPage, } = useManagementPage(props);
    const summaries = props.GetSummaries
        ? props.GetSummaries(state.rows, state.totalRecords)
        : props.Summaries;
    return (<section className="management-page">
      <HStack as="header" className="management-heading">
        <div>
          <p className="management-eyebrow">{props.Eyebrow}</p>
          <h1>{props.Title}</h1>
          <p>{props.Description}</p>
        </div>
        <div className="management-heading-actions">
          {(props.CanExport ?? true) && (<Button variant="outline" disabled={!filteredRows.length} onClick={() => exportRows(filteredRows, props.Title)}>
              <i className="fa-solid fa-download" aria-hidden="true"/> Export current page
            </Button>)}
          {(props.CanCreate ?? true) && (<Button onClick={openCreateDialog}>
              <i className="fa-solid fa-plus" aria-hidden="true"/>{" "}
              {props.AddLabel}
            </Button>)}
        </div>
      </HStack>

      <div className="management-summary">
        {summaries.map((summary) => (<article key={summary.Label}>
            <span className={`management-summary-icon management-tone-${summary.Tone.toLowerCase()}`}>
              <i className={`fa-solid ${summary.Icon}`} aria-hidden="true"/>
            </span>
            <div>
              <span>{summary.Label}</span>
              <strong>{state.loading || state.loadError ? "—" : summary.Value}</strong>
            </div>
          </article>))}
      </div>

      <article className="management-card">
        <div className="management-toolbar">
          <HStack className="management-search" gap="sm" align="center">
            <div className="management-search-input">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"/>
              <Input value={state.searchText} placeholder={props.SearchPlaceholder} onChange={(event) => setStateProperty("searchText", event.target.value)} onKeyDown={(event) => {
            if (event.key === "Enter")
                void search();
        }}/>
            </div>
            <Button type="button" variant="ghost" onClick={() => void search()}>
              Search
            </Button>
          </HStack>
          {props.Statuses && (<Dropdown value={state.status} options={toDropdownOptions(["All", ...props.Statuses], "All statuses")} onChange={(option) => setStateProperty("status", String(option.value))}/>)}
          {(props.Filters ?? []).map((filter) => (<Dropdown key={filter.Key} value={state.filters[filter.Key] ?? "All"} options={toDropdownOptions(["All", ...filter.Options], filter.Label)} onChange={(option) => setFilter(filter.Key, String(option.value))}/>))}
        </div>

        <div className="management-table-wrap">
          <table className="management-table">
            <thead>
              <tr>
                {props.Columns.map((column) => (<th key={column.Key}>{column.Header}</th>))}
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (<tr key={props.GetRowKey(row)} className={props.ActiveRowKey === props.GetRowKey(row) ? "management-row-active" : undefined} aria-selected={props.ActiveRowKey === props.GetRowKey(row) || undefined}>
                  {props.Columns.map((column) => (<td key={column.Key}>{column.Render(row)}</td>))}
                  <td className="management-row-actions"><div className="table-row-actions">
                    {props.RenderRowActions?.(row)}
                    {(props.CanEdit ?? true) &&
                (props.CanEditRow?.(row) ?? true) && (<button type="button" className="management-action" aria-label="Edit row" onClick={() => openEditDialog(row)}>
                          <i className="fa-solid fa-pen" aria-hidden="true"/>
                        </button>)}
                    {(props.CanDelete ?? true) &&
                (props.CanDeleteRow?.(row) ?? true) && (<button type="button" className="management-action management-action-delete" aria-label="Delete row" onClick={() => {
                    if (window.confirm("Delete this record?"))
                        void remove(row);
                }}>
                          <i className="fa-solid fa-trash" aria-hidden="true"/>
                        </button>)}
                  </div></td>
                </tr>))}
            </tbody>
          </table>
          {state.loading && <p className="management-empty" role="status">Loading records…</p>}
          {state.loadError && <div className="management-empty" role="alert">{state.loadError}<Button variant="outline" onClick={() => void search()}>Retry</Button></div>}
          {!state.loading && !state.loadError && filteredRows.length === 0 && (<div className="management-empty">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"/>
              <strong>No records found</strong>
              <span>Change the search or status filter and try again.</span>
            </div>)}
        </div>

        <footer className="management-footer">
          <span>
            Showing {state.totalRecords === 0 ? 0 : state.skip + 1}–
            {Math.min(state.skip + filteredRows.length, state.totalRecords)} of{" "}
            {state.totalRecords} records
          </span>
          <div>
            <button type="button" disabled={state.skip === 0} onClick={() => void previousPage()}>
              Previous
            </button>
            <button type="button" className="management-current-page">
              {Math.floor(state.skip / state.limit) + 1}
            </button>
            <button type="button" disabled={state.skip + state.limit >= state.totalRecords} onClick={() => void nextPage()}>
              Next
            </button>
          </div>
        </footer>
      </article>

      <RightSideDialogHTML Wide={Boolean(props.DialogSections)} IsOpen={state.isDialogOpen} Title={`${state.dialogMode} ${props.Title.replace(/ies$/, "y").replace(/s$/, "")}`} Description={state.dialogMode === "Create"
            ? `Enter the information required to create a new ${props.Title.toLowerCase().replace(/ies$/, "y").replace(/s$/, "")}.`
            : `Update the selected ${props.Title.toLowerCase().replace(/ies$/, "y").replace(/s$/, "")} information.`} FooterContent={props.DialogFooter?.(state.formValues)} SaveLabel={state.dialogMode === "Create" ? props.DialogSaveLabel ?? "Create" : "Save changes"} OnClose={closeDialog} OnSave={save} IsSaving={state.isSaving} Children={<div className={props.DialogSections ? "editor-layout" : "management-dialog-content"}>
            {(props.DialogSections ?? [{ Title: "", Keys: dialogFields.map(f => f.Key) }]).map(section => <Section key={section.Title} defaultOpen title={section.Title || "Details"} className={`admin-section editor-section ${section.Side ? "editor-side" : ""}`}>
            <div className="right-dialog-form">
            {dialogFields
                    .filter((_) => section.Keys.includes(_.Key) && (!_.VisibleWhen || _.VisibleWhen(state.formValues)) && (!state.selectedRow ||
                    (state.selectedRow?.DataState !== DataState.New &&
                        !_.RemoveAfterNew)))
                    .map((field) => {
                    const isNumericField = field.Type === "money" ||
                        field.Type === "integer" ||
                        field.Type === "decimal";
                    return (<div className={`right-dialog-field ${field.FullWidth ? "right-dialog-field-full" : ""}`} key={field.Key}>
                    <label htmlFor={`management-${field.Key}`}>
                      {field.Label}
                      {field.Required && <em>*</em>}
                    </label>
                    {field.Type === "datetime" || field.Type === "date" ? (<input id={`management-${field.Key}`} disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} className="fui-control" type={field.Type === "datetime" ? "datetime-local" : "date"} value={String(state.formValues[field.Key] ?? "")} required={field.Required} onChange={(event) => setFormValue(field.Key, event.target.value)}/>) : field.Type === "textarea" || field.Type === "json" ? (<TextArea disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} id={`management-${field.Key}`} value={field.Type === "json"
                                ? JSON.stringify(state.formValues[field.Key] ?? [], null, 2)
                                : String(state.formValues[field.Key] ?? "")} placeholder={field.Placeholder} required={field.Required} onChange={(event) => setFormValue(field.Key, isNumericField
                                ? event.target.value === ""
                                    ? ""
                                    : Number(event.target.value)
                                : event.target.value)}/>) : field.Type === "select" ? (<Dropdown disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} value={String(state.formValues[field.Key] ?? "")} placeholder={`Select ${field.Label.toLowerCase()}`} options={toDropdownOptions(field.Options ?? [])} onChange={(option) => setFormValue(field.Key, String(option.value))}/>) : field.Type === "multiselect" ? (<MultiSelect disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} value={Array.isArray(state.formValues[field.Key])
                                ? (state.formValues[field.Key] as string[])
                                : []} options={toDropdownOptions(field.Options ?? [])} placeholder={`Select ${field.Label.toLowerCase()}`} searchable showCheckboxes onChange={(values) => setFormValue(field.Key, values.map(String))}/>) : field.Type === "switch" ? (<Switch disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} name={field.Key} checked={Boolean(state.formValues[field.Key])} onChange={(checked) => setFormValue(field.Key, checked)}/>) : field.Type === "image" ? (<VStack gap="sm">
                        {typeof state.formValues.ImageUrl === "string" &&
                                state.formValues.ImageUrl && (<img className="management-image-preview" src={resolveImageUrl(String(state.formValues.ImageUrl ?? ""))} alt={field.Label}/>)}
                        <FileUpload accept="image/*" maxSize={10 * 1024 * 1024} preview onChange={(files) => {
                                const file = files[0];
                                if (!file)
                                    return;
                                void PrepareImageFile(file).then((prepared) => setFormValue(field.Key, prepared));
                            }}/>
                        <small>
                          Images are converted and capped at 100 KB before
                          upload.
                        </small>
                      </VStack>) : (<Input disabled={field.Disable || field.DisabledWhen?.(state.formValues) ||
                                (state.selectedRow && field.DisableAfterNew)} id={`management-${field.Key}`} type={inputType(field.Type)} inputMode={inputMode(field.Type)} currencySymbol={field.Type === "money" ? "₦" : undefined} style={{
                                border: field.Type === "money"
                                    ? "0px"
                                    : "1px solid #d8e2dc",
                            }} step={inputStep(field.Type)} value={isNumericField
                                ? String(state.formValues[field.Key] ?? "")
                                : String(state.formValues[field.Key] ?? "")} placeholder={field.Placeholder} required={field.Required} onChange={(event) => setFormValue(field.Key, isNumericField
                                ? event.target.value === ""
                                    ? ""
                                    : Number(event.target.value)
                                : event.target.value)}/>)}
                    {field.HelpText && <small>{field.HelpText}</small>}
                    {state.validationErrors[field.Key] && (<small className="right-dialog-field-error" role="alert">
                        {state.validationErrors[field.Key]}
                      </small>)}
                  </div>);
                })}
            </div>
            {section.Side && props.DialogSummary?.(state.formValues)}
            </Section>)}
            {props.RenderDialogContent && <Section defaultOpen title="Products" className={props.DialogSections ? "admin-section editor-section editor-products" : "admin-section right-dialog-field-full"}>
            {props.RenderDialogContent?.({
                FormValues: state.formValues,
                SetFormValue: setFormValue,
                DialogMode: state.dialogMode,
                ValidationErrors: state.validationErrors,
            })}
            </Section>}
            {props.DialogSummary && !props.DialogSections?.some(s => s.Side) && <aside className="editor-section editor-side">{props.DialogSummary(state.formValues)}</aside>}
          </div>}/>
      <Notification open={Boolean(state.successMessage)} type="success" title="Success" duration={3500} horizontal="right" vertical="top" onClose={() => setStateProperty("successMessage", "")}>
        <span className="success-message-text">{state.successMessage}</span>
      </Notification>
    </section>);
}
export function StatusBadge({ Status }: {
    Status: Status | string;
}) {
    return (<span className={`management-status management-status-${Status?.toLowerCase()}`}>
      {Status === "InActive" || Status === "In Active" ? "Inactive" : formatLabel(Status)}
    </span>);
}
export function IdentityCell({ Icon, Title, Subtitle, }: {
    Icon: string;
    Title: import("react").ReactNode;
    Subtitle: string;
}) {
    return (<HStack className="management-identity" align="center" gap="sm">
      <span>
        <i className={`fa-solid ${Icon}`} aria-hidden="true"/>
      </span>
      <VStack gap="xs">
        <strong>{Title}</strong>
        <small>{Subtitle}</small>
      </VStack>
    </HStack>);
}
function formatLabel(value: string): string {
  if(value === "InActive") return "Inactive";
    return value?.replace(/([a-z])([A-Z])/g, "$1 $2");
}
function toDropdownOptions(values: Array<string | DropdownModel>, allLabel?: string): DropdownModel[] {
    return values.map((option) => {
        if (typeof option !== "string")
            return option;
        return {
            display: option === "All" && allLabel ? allLabel : formatLabel(option),
            value: option,
            other: null,
        };
    });
}
function inputType(type: string | undefined): "text" | "email" | "tel" | "number" {
    if (type === "email" || type === "tel")
        return type;
    if (type === "integer" || type === "decimal" || type === "money")
        return "number";
    return "text";
}
function inputMode(type: string | undefined): "integer" | "decimal" | "money" | undefined {
    if (type === "integer")
        return "integer";
    if (type === "decimal")
        return "decimal";
    if (type === "money")
        return "money";
    return undefined;
}
function inputStep(type: string | undefined): number | undefined {
    if (type === "integer")
        return 1;
    if (type === "decimal" || type === "money")
        return 0.01;
    return undefined;
}
